import { Alert, Button, Dialog } from '@atoms'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import useMutation from '@hooks/useGQLMutation'
import { sendSmsMutation } from './mutations'
import { useErrorsStore } from '@stores/errorsStore'
import AppErrorsAlert from '@components/molecules/Alerts/AppErrorsAlert'

import { i18nValidation, scopedTranslation } from '@frontend/utils/I18n'
import { Textarea } from '@atoms/form/Textarea'
import { useState } from 'react'

const t = scopedTranslation('components.send_sms_modal')
const tAttributes = scopedTranslation('attributes.individual_sms')

const ERRORS_PATH = 'sendSms' as const

const schema = yup.object({
  body: yup
    .string()
    .label(tAttributes('body'))
    .required(i18nValidation('required'))
}).required()

type FormData = {
  body: string
}

type SendSmsModalProps = {
  open: boolean
  setOpen: (open: boolean) => void
  supporterId: string
  groupId: string
  supporterName: string
}

export default function SendSmsModal({
  open,
  setOpen,
  supporterId,
  groupId,
  supporterName,
}: SendSmsModalProps) {
  const { setErrors: setAppErrors, clearErrors: clearAppErrors } = useErrorsStore(({ setErrors, clearErrors }) => ({
    setErrors,
    clearErrors,
  }))

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: yupResolver(schema)
  })

  const [showSuccess, setShowSuccess] = useState(false)

  const sendSmsMutationHook = useMutation(sendSmsMutation)

  const onSubmit = (data: FormData) => {
    clearAppErrors(ERRORS_PATH)
    sendSmsMutationHook.mutate({
      supporterId,
      groupId,
      smsBody: data.body,
    }, {
      onSuccess: (response) => {
        if (response?.sendSms?.success) {
          setShowSuccess(true)
          reset()
        } else {
          setAppErrors({
            errorsPath: ERRORS_PATH,
            errors: response?.sendSms?.errors
          })
        }
      },
      onError: () => {
        setAppErrors({
          errorsPath: ERRORS_PATH,
          errors: [{
            message: t('errors.send_failed'),
            code: 'SENDSMS_ERROR'
          }]
        })
      }
    })
  }

  return (
    <Dialog.Root 
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          reset()
          clearAppErrors(ERRORS_PATH)
        }
        setOpen(nextOpen)
      }}
    >
      <Dialog.Content 
        size="xl" 
        data-testid="send-sms-modal"
        onPointerDownOutside={(e) => {
          e.preventDefault()
        }}
      >
        <Dialog.Header>
          <Dialog.Title>{t('title', { name: supporterName })}</Dialog.Title>
          <Dialog.Description className="tw-font-medium tw-pt-4">
            <AppErrorsAlert errorsPath={ERRORS_PATH} />
            {showSuccess && (
              <Alert type="success" title={t('alerts.success.title')}/>
            )}
          </Dialog.Description>
        </Dialog.Header>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='tw-pb-5' style={{ overflow: 'visible', height: 'auto' }}>
            <Textarea
              label={t('form.body_label')}
              errors={errors}
              placeholder={t('form.body_description')}
              {...register('body')}
            />
          </div>

          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button type="button" size="xl" rank="tertiary">
                {t('form.cancel')}
              </Button>
            </Dialog.Close>
            <Button 
              type="submit" 
              size="xl" 
              rank="primary"
              disabled={sendSmsMutationHook.isLoading}
            >
              {t('form.send_now')}
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
} 